/**
 * Pendo Analytics Service
 *
 * Handles Pendo agent initialization and event tracking.
 * The Pendo agent is loaded via the bootstrap snippet in index.html.
 */
import { PATIENT_LAUNCHED } from '../constants/pendoEvents';
import { PENDO_CONSENT_GROUP, ONETRUST_GROUPS_UPDATED_EVENT } from '../constants/pendo';
/**
 * Returns true when Pendo analytics is allowed to run.
 *
 * When OneTrust is not configured (e.g. local development) Pendo is not gated.
 * Otherwise consent requires the visitor to have opted in to the analytics
 * cookie group exposed via `window.OptanonActiveGroups`.
 */
export const hasPendoConsent = () => {
    var _a;
    const pendoWindow = window;
    // OneTrust not configured for this environment — do not block Pendo.
    if (!((_a = pendoWindow.__ENV) === null || _a === void 0 ? void 0 : _a.REACT_APP_ONETRUST_DOMAIN_SCRIPT_ID))
        return true;
    return typeof pendoWindow.OptanonActiveGroups === 'string' && pendoWindow.OptanonActiveGroups.includes(PENDO_CONSENT_GROUP);
};
/** Cached init options so Pendo can be initialized once consent is granted. */
let pendoInitOptions = null;
/** Guards against attaching the OneTrust consent listener more than once. */
let consentListenerAttached = false;
/**
 * Retry initialization when the visitor updates their OneTrust consent.
 * Registered lazily and only while consent is still pending.
 */
const attachConsentListener = () => {
    if (consentListenerAttached)
        return;
    consentListenerAttached = true;
    window.addEventListener(ONETRUST_GROUPS_UPDATED_EVENT, () => {
        if (pendoInitOptions && hasPendoConsent()) {
            initializePendo(pendoInitOptions);
        }
    });
};
/**
 * Initialize Pendo with visitor (practitioner) and account (tenant) info.
 * No-ops if Pendo is not loaded, already initialized, or consent is not granted.
 * When consent is pending, the call is deferred until OneTrust reports opt-in.
 */
export const initializePendo = (options) => {
    var _a, _b;
    const pendoWindow = window;
    if (!pendoWindow.pendo || !options.visitorId || pendoWindow.__PENDO_INITIALIZED__)
        return;
    if (!hasPendoConsent()) {
        pendoInitOptions = options;
        attachConsentListener();
        return;
    }
    // Load the Pendo agent now that consent is confirmed. Deferred until this
    // point when OneTrust gates analytics; a no-op if already loaded.
    (_a = pendoWindow.__loadPendoAgent__) === null || _a === void 0 ? void 0 : _a.call(pendoWindow);
    const pendoApiKey = pendoWindow.__PENDO_PUBLIC_APP_ID__ || ((_b = pendoWindow.__ENV) === null || _b === void 0 ? void 0 : _b.REACT_APP_PENDO_PUBLIC_APP_ID);
    pendoWindow.pendo.initialize(Object.assign(Object.assign({}, (pendoApiKey ? { apiKey: pendoApiKey } : {})), { visitor: {
            id: options.visitorId
        }, account: {
            id: options.accountId
        } }));
    pendoWindow.__PENDO_INITIALIZED__ = true;
};
/** Epoch ms when the patient launched event was last tracked. */
let patientLaunchedAt = null;
/** Returns the epoch ms recorded when patient launched was fired, or null. */
export const getPatientLaunchedAt = () => patientLaunchedAt;
/**
 * Track a custom Pendo event.
 * Automatically records the timestamp when eventName is the patient launched event.
 */
export const trackPendoEvent = (eventName, properties) => {
    var _a;
    if (!hasPendoConsent())
        return;
    const pendoWindow = window;
    if ((_a = pendoWindow.pendo) === null || _a === void 0 ? void 0 : _a.track) {
        if (eventName === PATIENT_LAUNCHED) {
            patientLaunchedAt = Date.now();
        }
        pendoWindow.pendo.track(eventName, properties);
    }
};
